import type { MaybeRefOrGetter } from 'vue';
import type { OnboardingPopupTaskRow } from '~/constants/onboarding-popup-tasks';
import { useTimeoutFn } from '@vueuse/core';
import { computed, nextTick, readonly, ref, toValue, watch, watchPostEffect } from 'vue';
import { useNuxtUiLocale, useSubmenuSafePath } from '#layers/ui/public';

const HOVER_OPEN_DELAY = 200;
const HOVER_CLOSE_DELAY = 100;
const FLYOUT_SIDE_OFFSET = 10;
const FLYOUT_COLLISION_PADDING = 8;
const MINIMUM_HORIZONTAL_INTENT_RATIO = 0.5;

interface UseOnboardingPopupFlyoutOptions {
	/** Ordered display rows; the first row is the fallback when the active ID is absent. */
	rows: MaybeRefOrGetter<OnboardingPopupTaskRow[]>;
	/** Becoming disabled closes the flyout and cancels pending opens and closes. */
	enabled: MaybeRefOrGetter<boolean>;
	/** Contains row buttons with `data-row-id` attributes, used to restore focus after Escape. */
	guideElement: MaybeRefOrGetter<HTMLElement | null | undefined>;
	/** Flyout bounds for pointer intent. */
	flyoutElement: MaybeRefOrGetter<HTMLElement | null | undefined>;
	/** Rendered action button; keyboard activation waits until this element is available. */
	actionElement: MaybeRefOrGetter<HTMLElement | SVGElement | null | undefined>;
}

/**
 * Coordinates desktop row previews, delayed hover opening/closing, and keyboard focus.
 * Selection can highlight a row before the flyout opens. Closing clears that highlight but retains
 * the active row so its content and focus target remain available during the popover's close lifecycle.
 *
 * Bind `open`, `content`, and `events.updateOpen` to the popover; `content` includes the autofocus
 * hooks that keep hover previews from stealing focus and return focus to the row after Escape.
 * Use `activateRow` for hover, `selectRow` for focus, and `clickRow` for activation. Track mouse
 * movement on both the row list and flyout so diagonal travel into the flyout preserves selection.
 * Keyboard activation focuses the action once it renders; pointer activation leaves focus alone.
 * Keep this in the popup's effect scope, with `enabled` reflecting desktop visibility and expansion.
 */
export function useOnboardingPopupFlyout(options: UseOnboardingPopupFlyoutOptions) {
	const nuxtUiLocale = useNuxtUiLocale();
	const open = ref(false);
	const activeRowId = ref<OnboardingPopupTaskRow['id']>('createdWorkspace');
	const showHighlight = ref(false);
	const focusActionOnOpen = ref(false);
	let restoreRowFocusOnClose = false;

	const highlightedRowId = computed(() => showHighlight.value ? activeRowId.value : null);
	const activeRow = computed(() => {
		const rows = toValue(options.rows);

		return rows.find((row) => row.id === activeRowId.value) ?? rows[0];
	});
	const side = computed(() => nuxtUiLocale.value.dir === 'rtl' ? 'left' as const : 'right' as const);
	const content = computed(() => ({
		side: side.value,
		align: 'end' as const,
		sideOffset: FLYOUT_SIDE_OFFSET,
		collisionPadding: FLYOUT_COLLISION_PADDING,
		onOpenAutoFocus: (event: Event) => event.preventDefault(),
		onCloseAutoFocus: restoreActiveRowFocus,
		onEscapeKeyDown: () => restoreRowFocusOnClose = true,
	}));
	const safePath = useSubmenuSafePath({
		submenuElement: options.flyoutElement,
		side,
		minimumHorizontalIntentRatio: MINIMUM_HORIZONTAL_INTENT_RATIO,
	});
	const {
		isPending: openPending,
		start: startOpen,
		stop: cancelOpen,
	} = useTimeoutFn(() => open.value = true, HOVER_OPEN_DELAY, { immediate: false });
	const { start: startClose, stop: cancelClose } = useTimeoutFn(close, HOVER_CLOSE_DELAY, { immediate: false });

	watch(() => toValue(options.enabled), (enabled) => {
		if (!enabled) close();
	});

	watchPostEffect(() => {
		if (!focusActionOnOpen.value || !open.value) return;

		const action = toValue(options.actionElement);
		if (!action) return;

		focusActionOnOpen.value = false;
		action.focus();
	});

	/** Ignores neighboring rows while the pointer is traveling toward the current flyout. */
	function activateRow(row: OnboardingPopupTaskRow, event: MouseEvent) {
		if (row.id !== activeRowId.value && safePath.isWithinSafePath(event)) return;

		selectRow(row);
	}

	/** Selects and highlights a row without opening the flyout or moving focus. */
	function selectRow(row: OnboardingPopupTaskRow) {
		activeRowId.value = row.id;
		showHighlight.value = true;
	}

	/** Opens immediately; a zero-detail click also requests focus on the rendered action. */
	function clickRow(row: OnboardingPopupTaskRow, event: MouseEvent) {
		focusActionOnOpen.value = event.detail === 0;
		selectRow(row);
		openNow();
	}

	function restoreActiveRowFocus(event: Event) {
		event.preventDefault();
		if (!restoreRowFocusOnClose) return;

		restoreRowFocusOnClose = false;

		void nextTick(() => {
			toValue(options.guideElement)?.querySelector<HTMLButtonElement>(`[data-row-id="${activeRowId.value}"]`)?.focus();
		});
	}

	function openNow() {
		clearTimers();
		open.value = true;
	}

	function scheduleOpen() {
		cancelClose();
		if (open.value || openPending.value) return;

		startOpen();
	}

	function scheduleClose() {
		cancelOpen();

		if (!open.value) {
			showHighlight.value = false;
			return;
		}

		startClose();
	}

	function close() {
		clearTimers();
		focusActionOnOpen.value = false;
		open.value = false;
		showHighlight.value = false;
		safePath.resetSafePath();
	}

	function clearTimers() {
		cancelOpen();
		cancelClose();
	}

	function updateOpen(value: boolean) {
		if (value) openNow();
		else close();
	}

	return {
		activeRow,
		close,
		content,
		highlightedRowId,
		open: readonly(open),
		events: {
			activateRow,
			cancelClose,
			clickRow,
			scheduleClose,
			scheduleOpen,
			selectRow,
			trackPointer: safePath.handlePointerMove,
			updateOpen,
		},
	};
}
